import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Typography,
    IconButton,
    Stack,
    Button,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import { useSelector } from "react-redux";
import { investorApi } from "../../api/api";
import { Autocomplete } from "@mui/material";
import { useNavigate } from "react-router-dom";

const MobileSearchDrawer = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cityQuery, setCityQuery] = useState("");
    const [addressQuery, setAddressQuery] = useState("");
    const navigate = useNavigate();
    const [typeQuery, setTypeQuery] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [minSqft, setMinSqft] = useState("");
    const [maxSqft, setMaxSqft] = useState("");


    const filteredProperties = properties.filter((item) => {
        const cityMatch = !cityQuery || item.city?.toLowerCase().includes(cityQuery.toLowerCase());
        const addressMatch = !addressQuery || item.address?.toLowerCase().includes(addressQuery.toLowerCase());
        const typeMatch = !typeQuery || item.type === typeQuery;
        const price = Number(item.MarketPrice || 0);
        const sqft = Number(item.SizeInSqFt || 0);
        const minPriceMatch = !minPrice || price >= Number(minPrice);
        const maxPriceMatch = !maxPrice || price <= Number(maxPrice);
        const minSqftMatch = !minSqft || sqft >= Number(minSqft);
        const maxSqftMatch = !maxSqft || sqft <= Number(maxSqft);

        return cityMatch && addressMatch && typeMatch && minPriceMatch && maxPriceMatch && minSqftMatch && maxSqftMatch;
    });

    const types = React.useMemo(() => {
        return [...new Set(properties.map(p => p.type).filter(Boolean))];
    }, [properties]);

    useEffect(() => {


        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await investorApi.getAllPropertiesAndProjects();
                setProperties(response.data);
            } catch (error) {
                console.error("Error fetching property data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const cities = React.useMemo(() => {
        const map = new Map();

        properties.forEach((p) => {
            if (p.city) {
                const key = p.city.trim().toLowerCase();
                if (!map.has(key)) {
                    map.set(key, p.city.trim());
                }
            }
        });

        return Array.from(map.values());
    }, [properties]);


    const addresses = React.useMemo(() => {
        const map = new Map();

        properties.forEach((p) => {
            if (p.address) {
                const key = p.address.trim().toLowerCase();
                if (!map.has(key)) {
                    map.set(key, p.address.trim());
                }
            }
        });

        return Array.from(map.values());
    }, [properties]);


    const handleSearch = () => {

        const dataToSend = {
            city: cityQuery,
            address: addressQuery,
            type: typeQuery,
            minPrice,
            maxPrice,
            minSqft,
            maxSqft,
            // filteredProperties,  
        };
        navigate("/search-properties", {
            state: dataToSend,
        });


    };


    const handleClearFilters = () => {
        setCityQuery("");
        setAddressQuery("");
        setTypeQuery("");
        setMinPrice("");
        setMaxPrice("");
        setMinSqft("");
        setMaxSqft("");
    };


    const propertyTypes = [
        { label: "Plot / Land", value: "property" },
        { label: "Project Plots", value: "project" },
        { label: "Independent House / Villa", value: "villa" },
        { label: "Flat / Apartment", value: "apartment" },
    ];


    const hasActiveFilters = cityQuery || addressQuery || typeQuery || minPrice || maxPrice || minSqft || maxSqft;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #fffdfd 0%, #fcfeff 100%)",
                pt: 2,
            }}
        >
            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    height: "100%",
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                        display: "none"
                    },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none"
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} color="primary.main">
                            Find Your Dream Property
                        </Typography>
                        {/* <Typography variant="body2" color="text.secondary">
                            Search with advanced filters
                        </Typography> */}
                    </Box>
                    <IconButton onClick={() => navigate(-1)}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Active Filters */}
                {hasActiveFilters && (
                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            {cityQuery && (
                                <Chip
                                    label={`City: ${cityQuery}`}
                                    onDelete={() => setCityQuery("")}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {addressQuery && (
                                <Chip
                                    label={`Address: ${addressQuery}`}
                                    onDelete={() => setAddressQuery("")}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {typeQuery && (
                                <Chip
                                    label={`Type: ${typeQuery}`}
                                    onDelete={() => setTypeQuery("")}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {(minPrice || maxPrice) && (
                                <Chip
                                    label={`Price: ${minPrice ? `$${minPrice}` : "Any"}-${maxPrice ? `$${maxPrice}` : "Any"}`}
                                    onDelete={() => { setMinPrice(""); setMaxPrice(""); }}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {(minSqft || maxSqft) && (
                                <Chip
                                    label={`Size: ${minSqft || "Any"}-${maxSqft || "Any"} sqft`}
                                    onDelete={() => { setMinSqft(""); setMaxSqft(""); }}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {hasActiveFilters && (
                                <Chip
                                    label="Clear All"
                                    onClick={handleClearFilters}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Search Fields */}
                <Stack spacing={3}>
                    {/* Location Section */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                            LOCATION
                        </Typography>
                        <Stack spacing={2}>
                            <Autocomplete
                                freeSolo
                                options={cities}
                                value={cityQuery}
                                onInputChange={(event, newInputValue) => {
                                    setCityQuery(newInputValue);
                                }}
                                loading={loading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="City"
                                        placeholder="Enter city name"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocationOnIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: 2,
                                                backgroundColor: "white",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "rgba(0, 0, 0, 0.12)",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "primary.main",
                                                },
                                            }
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                freeSolo
                                options={addresses}
                                value={addressQuery}
                                onInputChange={(event, newInputValue) => {
                                    setAddressQuery(newInputValue);
                                }}
                                loading={loading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Address / Location"
                                        placeholder="Enter exact address or area"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: 2,
                                                backgroundColor: "white",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "rgba(0, 0, 0, 0.12)",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "primary.main",
                                                },
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Property Type */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                            PROPERTY TYPE
                        </Typography>
                        <Autocomplete
                            options={propertyTypes}
                            value={propertyTypes.find((t) => t.value === typeQuery) || null}
                            onChange={(event, newValue) => {
                                setTypeQuery(newValue?.value || "");
                            }}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Property Type"
                                    placeholder="Select property type"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <HomeIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />

                    </Box>

                    <Divider />

                    {/* Price Range */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                            PRICE RANGE
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Min Price"
                                type="number"
                                fullWidth
                                value={minPrice}
                                onChange={(e) => setMinPrice(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            ₹
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        backgroundColor: "white",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgba(0, 0, 0, 0.12)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "primary.main",
                                        },
                                    }
                                }}
                            />
                            <TextField
                                label="Max Price"
                                type="number"
                                fullWidth
                                value={maxPrice}
                                onChange={(e) => setMaxPrice(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            ₹
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        backgroundColor: "white",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgba(0, 0, 0, 0.12)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "primary.main",
                                        },
                                    }
                                }}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Size Range */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                            SIZE (SQFT)
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Min Sqft"
                                type="number"
                                fullWidth
                                value={minSqft}
                                onChange={(e) => setMinSqft(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SquareFootIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        backgroundColor: "white",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgba(0, 0, 0, 0.12)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "primary.main",
                                        },
                                    }
                                }}
                            />
                            <TextField
                                label="Max Sqft"
                                type="number"
                                fullWidth
                                value={maxSqft}
                                onChange={(e) => setMaxSqft(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SquareFootIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        backgroundColor: "white",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgba(0, 0, 0, 0.12)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "primary.main",
                                        },
                                    }
                                }}
                            />
                        </Stack>
                    </Box>
                </Stack>


                {/* Action Buttons */}
                <Stack spacing={2} sx={{ mt: 4, mb: 5, bottom: 0, backgroundColor: "#f8fafc", pb: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "1rem",
                            textTransform: "none",
                            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                            },
                            transition: "all 0.2s ease",
                        }}
                    >
                        Search Properties
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleClearFilters}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: "0.9rem",
                            textTransform: "none",
                            borderWidth: 2,
                            "&:hover": {
                                borderWidth: 2,
                                backgroundColor: "rgba(0, 0, 0, 0.02)",
                            }
                        }}
                    >
                        Clear All Filters
                    </Button>
                </Stack>

                {/* Loading Overlay */}
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 999,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MobileSearchDrawer;